/* eslint-disable react/no-unknown-property */ // Three element를 unknown property로 오인식하므로 해당 코드 추가
import {
  ArrowCircleGray400Icon,
  ChevronDownPrimaryIcon,
  ChevronRightGray400Icon,
  EyeCrossLineGray400Icon,
  EyePrimaryIcon,
  XBlackIcon,
  XGray400Icon
} from '@assets/iconComponents';
import { Box, Button, Dialog, MenuItem, Select, styled } from '@mui/material';
import { AutosizeTextarea, ImageGenerateModal, ImageGenerateTextarea, LoadingSpinner, SecureImage, Text } from '..';
import { colorSet } from '@theme';
import { useFetchThreeDImageDetail, usePostImageGenerate } from '@api';
import { ICharacterSheetData, IThreeDImageCategory } from '@types';
import { downloadImage, makeSafeUrl } from '@utils';
import { useToast } from '@hooks';
import { styleConstant, ToastMessageEnum } from '@constant';
import { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import * as THREE from 'three';
import { Canvas, useLoader, useThree } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js';

interface IProps {
  isVisible: boolean;
  onClose: () => void;
  assetThreeDId: number | null;
}

// 3D 모델 컴포넌트
const Model = ({ url, onLoad, visibleObjects }: { url: string; onLoad?: () => void; visibleObjects?: Set<string> }) => {
  const obj = useLoader(OBJLoader, url);

  useEffect(() => {
    if (obj && visibleObjects) {
      // obj의 children을 순회하며 visibleObjects에 포함된 것만 표시
      obj.traverse((child) => {
        if (child instanceof THREE.Mesh || child instanceof THREE.Group) {
          child.visible = visibleObjects ? visibleObjects.has(child.name) || child.name === '' : true;
        }
      });
    }
  }, [obj, visibleObjects]);

  // 모델의 바운딩 박스를 계산하여 적절한 크기로 조정
  useEffect(() => {
    if (obj) {
      const box = new THREE.Box3().setFromObject(obj);
      const center = box.getCenter(new THREE.Vector3());
      const size = box.getSize(new THREE.Vector3());
      const maxDim = Math.max(size.x, size.y, size.z);
      const scale = 2.5 / maxDim;

      obj.scale.multiplyScalar(scale);
      obj.position.sub(center.multiplyScalar(scale));

      if (onLoad) {
        onLoad();
      }
    }
  }, [obj, onLoad]);

  return <primitive object={obj} scale={1} />;
};

// Canvas 내부에서 캡쳐 함수를 제공하는 컴포넌트
const CaptureHelper = forwardRef<{ capture: () => string | null }>((_, ref) => {
  const { gl, scene, camera } = useThree();

  useImperativeHandle(ref, () => ({
    capture: () => {
      try {
        // 현재 렌더링된 씬을 캡쳐
        gl.render(scene, camera);
        // Canvas의 데이터 URL 반환
        return gl.domElement.toDataURL('image/png');
      } catch (error) {
        console.error('캡쳐 실패:', error);
        return null;
      }
    }
  }));

  return null;
});

CaptureHelper.displayName = 'CaptureHelper';

// 3D 뷰어 컴포넌트
const ThreeDViewer = forwardRef<
  { capture: () => string | null },
  { assetFileUrl: string; visibleObjects?: Set<string> }
>(({ assetFileUrl, visibleObjects }, ref) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [isParsing, setIsParsing] = useState(true);
  const captureHelperRef = useRef<{ capture: () => string | null }>(null);

  useEffect(() => {
    const loadObjFile = async () => {
      try {
        // URL 인코딩 처리
        const safeUrl = makeSafeUrl(assetFileUrl);

        // fetch로 파일 가져오기 (쿠키 포함)
        const response = await fetch(safeUrl, {
          method: 'GET',
          credentials: 'include',
          headers: {
            Accept: 'application/octet-stream, text/plain, */*'
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to load OBJ file: ${response.status} ${response.statusText}`);
        }

        // Content-Type 확인
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('text/html')) {
          throw new Error('Received HTML instead of OBJ file. Check CORS and file URL.');
        }

        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setBlobUrl(url);
        setIsParsing(true); // 파싱 시작
      } catch (err) {
        console.error('Error loading OBJ file:', err);
      }
    };

    loadObjFile();

    // Cleanup: blob URL 해제
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [assetFileUrl]);

  const handleModelLoad = () => {
    setIsParsing(false); // 파싱 완료
  };

  // 부모 컴포넌트에 캡쳐 함수 노출
  useImperativeHandle(ref, () => ({
    capture: () => {
      return captureHelperRef.current?.capture() || null;
    }
  }));

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <Canvas camera={{ position: [0, 0, 3], fov: 50 }} style={{ width: '100%', height: '100%' }}>
        <ambientLight intensity={0.5} />
        <directionalLight position={[10, 10, 5]} intensity={1} />

        {blobUrl && <Model url={blobUrl} onLoad={handleModelLoad} visibleObjects={visibleObjects} />}

        <OrbitControls enableDamping dampingFactor={0.05} minDistance={0.5} maxDistance={15} />
        <CaptureHelper ref={captureHelperRef} />
      </Canvas>

      {/* 파일 로딩 및 파싱 중 LoadingSpinner 오버레이 */}
      {isParsing && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(50% / 2)',
            left: 'calc(50% / 2)',
            width: '50%',
            height: '50%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: colorSet.gray100,
            zIndex: styleConstant.zIndexModal + 1
          }}
        >
          <LoadingSpinner size={42} />
        </div>
      )}
    </div>
  );
});

ThreeDViewer.displayName = 'ThreeDViewer';

// 스크롤바 커스터마이징을 위해 MUI styled component 이용
const CharacterSheetContainer = styled('div')({
  height: 'calc(100% - 86px)',
  overflowY: 'auto',

  '&::-webkit-scrollbar': {
    width: '8px',
    height: '0px'
  },

  '&::-webkit-scrollbar-thumb': {
    borderRadius: '8px',
    backgroundColor: colorSet.primaryMain
  },

  '&::-webkit-scrollbar-track': {
    borderRadius: '8px',
    backgroundColor: colorSet.gray200
  }
});

export const ThreeDImageModal = (props: IProps) => {
  const { isVisible, onClose, assetThreeDId } = props;

  const { showToast } = useToast();

  const [isThreeDMode, setIsThreeDMode] = useState<boolean>(false);
  // 3D 오브젝트 리스트 및 상태 관리
  const [objectList, setObjectList] = useState<string[]>([]); // ['인물1', '인물2', '물건1']
  const [objectVisibility, setObjectVisibility] = useState<Record<string, boolean>>({}); // { '인물1': true, '인물2': true, '물건1': true }
  const [characterSheetData, setCharacterSheetData] = useState<ICharacterSheetData>({ A: null, B: null, C: null });
  const [characterSheetPreviewUrls, setCharacterSheetPreviewUrls] = useState<{
    A: string | null;
    B: string | null;
    C: string | null;
  }>({ A: null, B: null, C: null });
  const [mainPrompt, setMainPrompt] = useState<string>('');
  const [aiModel, setAiModel] = useState<string>('pro');
  const [isImageGenerateModalVisible, setIsImageGenerateModalVisible] = useState<boolean>(false);

  const threeDViewerRef = useRef<{ capture: () => string | null }>(null);
  const generateButtonContainerRef = useRef<HTMLDivElement>(null);

  const { data } = useFetchThreeDImageDetail(assetThreeDId);

  const { mutateAsync, isPending, data: initialData } = usePostImageGenerate();

  // 카테고리 경로를 추출하는 헬퍼 함수
  // category는 단일 객체이고, 재귀적으로 children을 탐색하여 경로 추출
  const getCategoryPathArr = (category: IThreeDImageCategory | undefined): string[] => {
    if (!category) return [];

    const path: string[] = [];

    // 재귀적으로 카테고리 트리를 탐색하여 경로 수집
    const traverseCategory = (cat: IThreeDImageCategory): void => {
      path.push(cat.nameKo);

      // children이 있으면 첫 번째 자식으로 재귀
      if (cat.children && cat.children.length > 0) {
        traverseCategory(cat.children[0]);
      }
    };

    // 루트 카테고리부터 시작
    traverseCategory(category);

    return path;
  };

  // data?.category는 단일 객체
  const categoryPathArr = getCategoryPathArr(data?.category);

  // 3D 모델 캡쳐 및 클립보드 복사 함수
  const handleCopy3DImage = async () => {
    if (!threeDViewerRef.current) {
      console.log('Failed to copy image');
      return;
    }

    const dataUrl = threeDViewerRef.current.capture();
    if (!dataUrl) {
      console.log('Failed to copy image');
      return;
    }

    try {
      // Data URL을 Blob으로 변환
      const response = await fetch(dataUrl);
      const blob = await response.blob();

      // Clipboard API를 사용하여 클립보드에 복사
      if (navigator.clipboard && navigator.clipboard.write) {
        const item = new ClipboardItem({
          'image/png': blob
        });
        await navigator.clipboard.write([item]);
        showToast(ToastMessageEnum.IMAGE_COPY_SUCCESS);
      } else {
        // Clipboard API를 지원하지 않는 경우
        console.log('Clipboard API is not supported');
      }
    } catch (error) {
      console.log('Failed to copy image:', error);
    }
  };

  // 3D 모델 캡쳐 및 저장 함수
  const handleSave3DImage = async () => {
    if (!threeDViewerRef.current) {
      console.log('Failed to download image');
      return;
    }

    const dataUrl = threeDViewerRef.current.capture();
    if (!dataUrl) {
      console.log('Failed to download image');
      return;
    }

    try {
      // Data URL을 Blob으로 변환
      const response = await fetch(dataUrl);
      const blob = await response.blob();

      // 기본 파일명 설정
      const defaultFileName = data?.originFileName ?? 'refa_image';

      // File System Access API 사용 (브라우저 파일 탐색기 열기)
      if ('showSaveFilePicker' in window) {
        try {
          const fileHandle = await (window as any).showSaveFilePicker({
            suggestedName: defaultFileName,
            types: [
              {
                description: 'PNG Image',
                accept: {
                  'image/png': ['.png']
                }
              }
            ]
          });

          // 파일 저장
          const writable = await fileHandle.createWritable();
          await writable.write(blob);
          await writable.close();

          showToast(ToastMessageEnum.IMAGE_DOWNLOAD_SUCCESS);
        } catch (error: any) {
          // 사용자가 다이얼로그를 취소한 경우
          if (error.name === 'AbortError') {
            return; // 취소 시 아무것도 하지 않음
          }
          // 다른 에러 발생 시 fallback 사용
          throw error;
        }
      } else {
        // File System Access API를 지원하지 않는 브라우저의 경우 fallback
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = defaultFileName;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        showToast(ToastMessageEnum.IMAGE_DOWNLOAD_SUCCESS);
      }
    } catch (error) {
      console.log('Failed to download image:', error);
    }
  };

  // visibleObjects 계산 (보임 상태인 오브젝트들만)
  const visibleObjects = new Set(objectList.filter((objName) => objectVisibility[objName]));

  // Eye 아이콘 클릭 핸들러 (보임/숨김 토글)
  const handleEyeIconClick = (e: React.MouseEvent, index: number) => {
    e.stopPropagation(); // 이벤트 전파 방지
    const objectName = objectList[index];
    setObjectVisibility((prev) => ({
      ...prev,
      [objectName]: !prev[objectName]
    }));
  };

  const handleCharacterSheetReset = () => {
    setCharacterSheetData({ A: null, B: null, C: null });
    setCharacterSheetPreviewUrls({ A: null, B: null, C: null });
    setMainPrompt('');
  };

  const handleSubPromptChange = (slot: 'A' | 'B' | 'C', value: string) => {
    setCharacterSheetData((prev) => ({
      ...prev,
      [slot]: prev[slot]
        ? {
            ...prev[slot],
            subPrompt: value === '' ? null : value
          }
        : null
    }));
  };

  const handleAddCharacterSheet = (slot: 'A' | 'B' | 'C') => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        if (characterSheetPreviewUrls[slot]) {
          URL.revokeObjectURL(characterSheetPreviewUrls[slot]!);
        }
        const newUrl = URL.createObjectURL(file);

        setCharacterSheetData((prev) => ({
          ...prev,
          [slot]: { imageData: file, subPrompt: null }
        }));
        setCharacterSheetPreviewUrls((prev) => ({
          ...prev,
          [slot]: newUrl
        }));
      }
    };
    input.click();
  };

  const handleAddNextCharacterSheet = () => {
    if (!characterSheetData.B) {
      handleAddCharacterSheet('B');
    } else if (!characterSheetData.C) {
      handleAddCharacterSheet('C');
    }
  };

  const handleDeleteCharacterSheet = (slot: 'A' | 'B' | 'C') => {
    const slots: ('A' | 'B' | 'C')[] = ['A', 'B', 'C'];

    // 현재 존재하는 아이템들만 수집 (삭제할 슬롯 제외)
    const remainingItems = slots
      .filter((s) => s !== slot && characterSheetData[s] !== null)
      .map((s) => characterSheetData[s]!);

    // 모든 기존 URL 해제
    slots.forEach((s) => {
      if (characterSheetPreviewUrls[s]) {
        URL.revokeObjectURL(characterSheetPreviewUrls[s]!);
      }
    });

    // 새로운 상태 구성
    const newData: ICharacterSheetData = { A: null, B: null, C: null };
    const newUrls: { A: string | null; B: string | null; C: string | null } = { A: null, B: null, C: null };

    remainingItems.forEach((item, index) => {
      const targetSlot = slots[index];
      newData[targetSlot] = item;
      // File 객체로부터 새 URL 생성
      newUrls[targetSlot] = URL.createObjectURL(item.imageData);
    });

    setCharacterSheetData(newData);
    setCharacterSheetPreviewUrls(newUrls);
  };

  const handleGenerateButtonClick = async () => {
    setIsImageGenerateModalVisible(true);

    // 3D 모델 캡쳐하여 File 객체 생성
    let modelImageFile: File | undefined = undefined;
    if (threeDViewerRef.current) {
      const dataUrl = threeDViewerRef.current.capture();
      if (dataUrl) {
        try {
          const response = await fetch(dataUrl);
          const blob = await response.blob();
          const fileName = data?.originFileName?.replace(/\.[^/.]+$/, '') || '3d_model';
          modelImageFile = new File([blob], `${fileName}.png`, { type: 'image/png' });
        } catch (error) {
          console.error('Failed to convert 3D image to File:', error);
        }
      }
    }

    // FormData 생성
    const formData = new FormData();

    // characterSheetData 추가 (A, B, C 모두 명시적으로 처리)
    const slots: ('A' | 'B' | 'C')[] = ['A', 'B', 'C'];
    slots.forEach((slot) => {
      const sheetData = characterSheetData[slot];
      if (sheetData) {
        formData.append(`characterSheetData[${slot}][imageData]`, sheetData.imageData);
        formData.append(`characterSheetData[${slot}][subPrompt]`, sheetData.subPrompt ?? 'null');
      } else {
        // null인 경우 명시적으로 null 표시
        formData.append(`characterSheetData[${slot}]`, 'null');
      }
    });

    // modelImageData 추가
    if (modelImageFile) {
      formData.append('modelImageData', modelImageFile);
    }

    // mainPrompt 추가
    formData.append('mainPrompt', mainPrompt);

    // 나노바나나 모델 추가
    formData.append('aiModel', aiModel);

    await mutateAsync({ body: formData });
  };

  // OBJ 파일에서 오브젝트 리스트 추출
  useEffect(() => {
    if (data?.assetFileUrl && isThreeDMode) {
      const loadAndParseObj = async () => {
        try {
          const safeUrl = makeSafeUrl(data.assetFileUrl);
          const response = await fetch(safeUrl, {
            method: 'GET',
            credentials: 'include',
            headers: {
              Accept: 'application/octet-stream, text/plain, */*'
            }
          });

          if (!response.ok) {
            setObjectList([]);
            setObjectVisibility({});
            return;
          }

          const text = await response.text();
          const lines = text.split('\n');
          const objects: string[] = [];
          const visibility: Record<string, boolean> = {};

          // OBJ 파일에서 'o ' 또는 'g ' 키워드로 시작하는 라인 찾기
          lines.forEach((line) => {
            const trimmed = line.trim();
            if (trimmed.startsWith('o ') || trimmed.startsWith('g ')) {
              const objectName = trimmed.substring(2).trim();
              if (objectName && !objects.includes(objectName)) {
                objects.push(objectName);
                visibility[objectName] = true; // 기본적으로 보임
              }
            }
          });

          // 오브젝트를 찾았으면 설정, 없으면 빈 배열
          setObjectList(objects);
          setObjectVisibility(visibility);
        } catch (error) {
          console.error('Error parsing OBJ file:', error);
          // 파싱 실패 시 빈 배열로 설정
          setObjectList([]);
          setObjectVisibility({});
        }
      };

      loadAndParseObj();
    } else {
      // 3D 모드가 아니거나 assetFileUrl이 없으면 초기화
      setObjectList([]);
      setObjectVisibility({});
    }
  }, [data?.assetFileUrl, isThreeDMode]);

  // 모달 닫았다가 다시 열 때 상태 초기화
  useEffect(() => {
    if (isVisible) {
      setIsThreeDMode(false);
      setObjectList([]);
      setObjectVisibility({});
      setCharacterSheetData({ A: null, B: null, C: null });
      setCharacterSheetPreviewUrls({ A: null, B: null, C: null });
      setMainPrompt('');
      setAiModel('pro');
    }
  }, [isVisible]);

  // 컴포넌트 언마운트 시 URL 해제를 위한 useEffect 추가
  useEffect(() => {
    return () => {
      Object.values(characterSheetPreviewUrls).forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, [characterSheetPreviewUrls]);

  // characterSheetData 변경 시 mainPrompt 자동 업데이트
  useEffect(() => {
    const hasA = characterSheetData.A !== null;
    const hasB = characterSheetData.B !== null;
    const hasC = characterSheetData.C !== null;

    if (!hasA && !hasB && !hasC) {
      // 모두 없을 때
      setMainPrompt('');
    } else if (hasA && !hasB && !hasC) {
      // A만 존재
      setMainPrompt('Change the pose of the person in Figure 2 to that of Figure 1.');
    } else if (hasA && hasB && !hasC) {
      // A, B 존재
      setMainPrompt('{A}를 {레퍼런스 1} 포즈로 그려주고,\n{B}를 {레퍼런스 2} 포즈로 그려줘.');
    } else if (hasA && hasB && hasC) {
      // A, B, C 모두 존재
      setMainPrompt(
        '{A}를 {레퍼런스 1} 포즈로 그려주고,\n{B}를 {레퍼런스 2} 포즈로 그려주고,\n{C}를 {레퍼런스 3} 포즈로 그려줘.'
      );
    }
  }, [characterSheetPreviewUrls]);

  return (
    <>
      <Dialog
        open={isVisible}
        onClose={onClose}
        scroll="body"
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.3)'
          },
          '& .MuiDialog-paper': {
            width: '100%',
            maxWidth: 'calc(100% - 288px)',
            height: 'calc(100% - 80px)',
            maxHeight: 'calc(100% - 80px)',
            margin: '40px 144px',
            padding: '60px 40px',
            boxShadow: '0px 4px 24px rgba(0, 0, 0, 0.06)',
            borderRadius: 0
          }
        }}
      >
        <XBlackIcon
          style={{ width: 24, height: 24, position: 'absolute', top: 24, right: 24, cursor: 'pointer' }}
          onClick={onClose}
        />

        {/* 폴더 경로 */}
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginBottom: '12px' }}>
          {categoryPathArr.map((item, index) => {
            return (
              <div key={index} style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                <Text variant="body1" color={colorSet.gray500} marginRight="10px">
                  {item}
                </Text>
                <ChevronRightGray400Icon style={{ marginRight: '10px' }} />
              </div>
            );
          })}

          <Text variant="body1" color={colorSet.gray500}>
            {data?.originFileName}
          </Text>
        </div>

        <div
          style={{
            width: '100%',
            height: 'calc(100% - 32px)',
            borderRadius: '6px',
            border: `1px solid ${colorSet.gray300}`,
            display: 'flex',
            flexDirection: 'row'
          }}
        >
          {/* 좌측 */}
          <div
            style={{
              width: 230,
              height: '100%',
              padding: '24px 16px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between'
            }}
          >
            {/* 이미지-3D 전환 토글 */}
            {data && data.assetFileUrl ? (
              <div
                onClick={() => {
                  setIsThreeDMode(!isThreeDMode);
                }}
                style={{
                  width: 198,
                  height: 40,
                  borderRadius: 20,
                  border: `1px solid ${colorSet.gray400}`,
                  backgroundColor: colorSet.white,
                  display: 'flex',
                  flexDirection: 'row',
                  padding: 2,
                  cursor: 'pointer'
                }}
              >
                <div
                  style={{
                    width: 96,
                    height: 34,
                    borderRadius: 17,
                    backgroundColor: isThreeDMode ? colorSet.white : colorSet.gray600,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  <Text variant="body1" color={isThreeDMode ? colorSet.gray600 : colorSet.white}>
                    이미지
                  </Text>
                </div>
                <div
                  style={{
                    width: 96,
                    height: 34,
                    borderRadius: 17,
                    backgroundColor: isThreeDMode ? colorSet.gray600 : colorSet.white,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  <Text variant="body1" color={isThreeDMode ? colorSet.white : colorSet.gray600}>
                    3D
                  </Text>
                </div>
              </div>
            ) : (
              <div />
            )}

            {data && data.assetFileUrl && isThreeDMode && (
              <div
                style={{
                  height: 'calc(100% - 88px)',
                  marginTop: '12px',
                  marginBottom: '32px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between'
                }}
              >
                <div
                  style={{
                    flex: '1 1 0',
                    minHeight: 0,
                    overflowY: 'auto'
                  }}
                >
                  {objectList.map((objectName, index) => {
                    const isVisible = objectVisibility[objectName];
                    return (
                      <div
                        key={index}
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '10px 0px'
                        }}
                      >
                        <Text variant="body2">Model {index + 1}</Text>
                        <div
                          style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                          onClick={(e) => handleEyeIconClick(e, index)}
                        >
                          {isVisible ? <EyePrimaryIcon /> : <EyeCrossLineGray400Icon />}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div style={{ marginTop: '32px' }}>
                  <Button
                    onClick={handleCopy3DImage}
                    variant="outlined"
                    color="secondary"
                    sx={{
                      width: '198px',
                      height: '44px',
                      marginBottom: '12px'
                    }}
                  >
                    캡쳐
                  </Button>
                  <Button
                    onClick={handleSave3DImage}
                    variant="outlined"
                    color="secondary"
                    sx={{ width: '198px', height: '44px', marginBottom: '8px' }}
                  >
                    저장
                  </Button>
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Text variant="body1" color={colorSet.warningMain}>
                      설정한 구도로 캡쳐 및 저장됩니다.
                    </Text>
                  </div>
                </div>
              </div>
            )}

            <Button
              onClick={() => {
                downloadImage(isThreeDMode ? data?.assetFileUrl : data?.previewImageUrl, data?.originFileName).then(
                  (isSuccess) => {
                    if (isSuccess) {
                      showToast(ToastMessageEnum.IMAGE_DOWNLOAD_SUCCESS);
                    }
                  }
                );
              }}
              variant="contained"
              color="primary"
              sx={{
                width: 198,
                height: 44,
                minHeight: 44,
                flexShrink: 0,
                textTransform: 'none'
              }}
            >
              파일 다운로드 {isThreeDMode ? '(obj)' : '(jpg)'}
            </Button>
          </div>

          {/* 이미지 모드: 우측 이미지 */}
          {data && data.previewImageUrl && !isThreeDMode && (
            <div style={{ width: 'calc(100% - 230px)', backgroundColor: colorSet.gray100 }}>
              <SecureImage
                imageUrl={data.previewImageUrl}
                imageStyle={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderTopRightRadius: '6px',
                  borderBottomRightRadius: '6px'
                }}
              />
            </div>
          )}
          {/* 3D 모드: 중앙 3D 모델 & 우측 메뉴 */}
          {data && data.assetFileUrl && isThreeDMode && (
            <div style={{ width: 'calc(100% - 230px)', display: 'flex', flexDirection: 'row' }}>
              <div
                style={{
                  width: 'calc(100% - 334px)',
                  backgroundColor: colorSet.gray100,
                  borderLeft: `1px solid ${colorSet.gray300}`,
                  borderRight: `1px solid ${colorSet.gray300}`
                }}
              >
                <ThreeDViewer ref={threeDViewerRef} assetFileUrl={data.assetFileUrl} visibleObjects={visibleObjects} />
              </div>
              <div style={{ width: '334px' }}>
                <div
                  style={{
                    height: '74px',
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '24px 16px 18px 16px'
                  }}
                >
                  <Text variant="h2" fontWeight="bold">
                    이미지 생성
                  </Text>
                  <ArrowCircleGray400Icon
                    onClick={handleCharacterSheetReset}
                    style={{ width: 24, height: 24, cursor: 'pointer' }}
                  />
                </div>
                {!characterSheetData.A && !characterSheetData.B && !characterSheetData.C ? (
                  <div style={{ padding: '0px 16px' }}>
                    <Text variant="body1" style={{ marginBottom: '18px' }}>
                      캐릭터 시트를 추가해서 이미지를 생성하세요.
                    </Text>
                    <Button
                      onClick={() => handleAddCharacterSheet('A')}
                      variant="outlined"
                      color="primary"
                      sx={{ width: '100%' }}
                    >
                      캐릭터시트 추가
                    </Button>
                  </div>
                ) : (
                  <div
                    style={{
                      height: 'calc(100% - 74px)',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between'
                    }}
                  >
                    <CharacterSheetContainer>
                      <div style={{ padding: '0px 16px 12px 16px', borderBottom: `1px solid ${colorSet.gray200}` }}>
                        <Text variant="subtitle" fontWeight="bold" style={{ marginBottom: '6px' }}>
                          캐릭터 시트
                        </Text>
                        <Text variant="body2" color={colorSet.gray500}>
                          캐릭터 보조 프롬프트는 선택사항입니다.
                        </Text>
                      </div>
                      <div style={{ marginBottom: '16px' }}>
                        {/* A 아이템 */}
                        {characterSheetData.A && (
                          <div
                            style={{
                              width: '100%',
                              backgroundColor: colorSet.gray100,
                              borderBottom: `1px solid ${colorSet.gray200}`,
                              padding: '10px 16px'
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start',
                                marginBottom: '10px'
                              }}
                            >
                              <div
                                style={{
                                  width: 84,
                                  height: 84,
                                  border: `1px solid ${colorSet.gray300}`,
                                  position: 'relative'
                                }}
                              >
                                <div
                                  style={{
                                    width: 24,
                                    height: 24,
                                    backgroundColor: colorSet.white,
                                    border: `1px solid ${colorSet.primaryMain}`,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    position: 'absolute',
                                    top: 0,
                                    left: 0
                                  }}
                                >
                                  <Text variant="body1" fontWeight="bold" color={colorSet.primaryMain}>
                                    A
                                  </Text>
                                </div>
                                <img
                                  src={characterSheetPreviewUrls.A ?? undefined}
                                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                              </div>
                              <XGray400Icon
                                style={{ width: 16, height: 16, cursor: 'pointer' }}
                                onClick={() => handleDeleteCharacterSheet('A')}
                              />
                            </div>
                            <AutosizeTextarea
                              placeholder="의상, 소품, 표정, 동작 등을 변경하세요."
                              style={{ backgroundColor: colorSet.white }}
                              value={characterSheetData.A?.subPrompt ?? ''}
                              onChange={(e) => handleSubPromptChange('A', e.target.value)}
                            />
                          </div>
                        )}
                        {/* B 아이템 */}
                        {characterSheetData.B && (
                          <div
                            style={{
                              width: '100%',
                              backgroundColor: colorSet.gray100,
                              borderBottom: `1px solid ${colorSet.gray200}`,
                              padding: '10px 16px'
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start',
                                marginBottom: '10px'
                              }}
                            >
                              <div
                                style={{
                                  width: 84,
                                  height: 84,
                                  border: `1px solid ${colorSet.gray300}`,
                                  position: 'relative'
                                }}
                              >
                                <div
                                  style={{
                                    width: 24,
                                    height: 24,
                                    backgroundColor: colorSet.white,
                                    border: `1px solid ${colorSet.primaryMain}`,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    position: 'absolute',
                                    top: 0,
                                    left: 0
                                  }}
                                >
                                  <Text variant="body1" fontWeight="bold" color={colorSet.primaryMain}>
                                    B
                                  </Text>
                                </div>
                                <img
                                  src={characterSheetPreviewUrls.B ?? undefined}
                                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                              </div>
                              <XGray400Icon
                                style={{ width: 16, height: 16, cursor: 'pointer' }}
                                onClick={() => handleDeleteCharacterSheet('B')}
                              />
                            </div>
                            <AutosizeTextarea
                              placeholder="의상, 소품, 표정, 동작 등을 변경하세요."
                              style={{ backgroundColor: colorSet.white }}
                              value={characterSheetData.B?.subPrompt ?? ''}
                              onChange={(e) => handleSubPromptChange('B', e.target.value)}
                            />
                          </div>
                        )}
                        {/* C 아이템 */}
                        {characterSheetData.C && (
                          <div
                            style={{
                              width: '100%',
                              backgroundColor: colorSet.gray100,
                              borderBottom: `1px solid ${colorSet.gray200}`,
                              padding: '10px 16px'
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start',
                                marginBottom: '10px'
                              }}
                            >
                              <div
                                style={{
                                  width: 84,
                                  height: 84,
                                  border: `1px solid ${colorSet.gray300}`,
                                  position: 'relative'
                                }}
                              >
                                <div
                                  style={{
                                    width: 24,
                                    height: 24,
                                    backgroundColor: colorSet.white,
                                    border: `1px solid ${colorSet.primaryMain}`,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    position: 'absolute',
                                    top: 0,
                                    left: 0
                                  }}
                                >
                                  <Text variant="body1" fontWeight="bold" color={colorSet.primaryMain}>
                                    C
                                  </Text>
                                </div>
                                <img
                                  src={characterSheetPreviewUrls.C ?? undefined}
                                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                              </div>
                              <XGray400Icon
                                style={{ width: 16, height: 16, cursor: 'pointer' }}
                                onClick={() => handleDeleteCharacterSheet('C')}
                              />
                            </div>
                            <AutosizeTextarea
                              placeholder="의상, 소품, 표정, 동작 등을 변경하세요."
                              style={{ backgroundColor: colorSet.white }}
                              value={characterSheetData.C?.subPrompt ?? ''}
                              onChange={(e) => handleSubPromptChange('C', e.target.value)}
                            />
                          </div>
                        )}
                      </div>
                      {/* B, C 모두 있으면 버튼 숨김 */}
                      {(!characterSheetData.B || !characterSheetData.C) && (
                        <div style={{ paddingLeft: '16px', marginBottom: '24px' }}>
                          <Button
                            onClick={handleAddNextCharacterSheet}
                            variant="outlined"
                            color="secondary"
                            sx={{ width: '127px', height: '32px' }}
                          >
                            <Text variant="body2" color={colorSet.gray400}>
                              캐릭터시트 추가
                            </Text>
                          </Button>
                        </div>
                      )}
                      <div style={{ padding: '0px 16px' }}>
                        <Text variant="subtitle" fontWeight="bold" style={{ marginBottom: '8px' }}>
                          {characterSheetData.C ? '3인' : characterSheetData.B ? '2인' : '1인'} 기본 프롬프트
                        </Text>
                        <ImageGenerateTextarea
                          defaultRow={characterSheetData.C ? 3 : 2}
                          placeholder={
                            characterSheetData.C
                              ? `{A}를 {레퍼런스 1} 포즈로 그려주고,
{B}를 {레퍼런스 2} 포즈로 그려주고,
{C}를 {레퍼런스 3} 포즈로 그려줘.`
                              : characterSheetData.B
                                ? `{A}를 {레퍼런스 1} 포즈로 그려주고,
{B}를 {레퍼런스 2} 포즈로 그려줘.`
                                : `Change the pose of the person in Figure 2 to that of Figure 1.`
                          }
                          value={mainPrompt}
                          onChange={(e) => {
                            setMainPrompt(e.target.value);
                          }}
                        />
                      </div>
                    </CharacterSheetContainer>
                    <div style={{ height: '86px', padding: '18px 16px 24px 16px' }}>
                      <Box ref={generateButtonContainerRef} sx={{ display: 'flex', alignItems: 'center' }}>
                        <Select
                          value={aiModel}
                          onChange={(e) => setAiModel(e.target.value)}
                          renderValue={(value) => {
                            if (value === 'pro') return 'Pro';
                            if (value === '1') return '1.0';
                            return value;
                          }}
                          IconComponent={(props) => (
                            <ChevronDownPrimaryIcon
                              style={{
                                width: 14,
                                height: 14,
                                position: 'absolute',
                                right: '11px',
                                pointerEvents: 'none'
                              }}
                            />
                          )}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                width: generateButtonContainerRef.current?.offsetWidth || 'auto',
                                borderRadius: 0
                              }
                            },
                            anchorOrigin: {
                              vertical: 'bottom',
                              horizontal: 'left'
                            },
                            transformOrigin: {
                              vertical: 'top',
                              horizontal: 'left'
                            }
                          }}
                          sx={{
                            width: '63px',
                            height: '44px',
                            cursor: 'pointer',
                            backgroundColor: colorSet.white,
                            color: colorSet.primaryMain,
                            border: `1px solid ${colorSet.primaryMain}`,
                            borderRadius: '0px',
                            '& .MuiOutlinedInput-notchedOutline': {
                              border: 'none'
                            },
                            '& .MuiSelect-select': {
                              padding: '0 8px',
                              fontSize: '14px',
                              display: 'flex',
                              alignItems: 'center'
                            },
                            '& .MuiSelect-icon': {
                              color: colorSet.primaryMain,
                              right: '4px'
                            }
                          }}
                        >
                          <MenuItem
                            value="1"
                            sx={{
                              height: '42px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '14px'
                            }}
                          >
                            나노바나나 1.0
                          </MenuItem>
                          <MenuItem
                            value="pro"
                            sx={{
                              height: '42px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '14px'
                            }}
                          >
                            나노바나나 pro
                          </MenuItem>
                        </Select>
                        <Button
                          disabled={mainPrompt.trim().length === 0}
                          onClick={() => {
                            handleGenerateButtonClick();
                          }}
                          variant="contained"
                          sx={{
                            width: 'calc(100% - 63px)',
                            height: '44px',
                            backgroundColor:
                              mainPrompt.trim().length === 0 ? colorSet.gray300 : colorSet.gradientPrimaryMain,
                            color: colorSet.white
                          }}
                        >
                          생성
                        </Button>
                      </Box>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </Dialog>

      <ImageGenerateModal
        isVisible={isImageGenerateModalVisible}
        onClose={() => {
          setIsImageGenerateModalVisible(false);
        }}
        isInitialPending={isPending}
        initialData={initialData?.data.data}
        initialAiModel={aiModel}
      />
    </>
  );
};
